import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  isStaleChunk: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isStaleChunk: false };
  }

  static getDerivedStateFromError(error: Error): State {
    const message = error instanceof Error ? error.message : String(error);
    const isStaleChunk =
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Importing a module script failed') ||
      message.includes('Expected a JavaScript-or-Wasm module script') ||
      message.includes('Unexpected token <') ||
      message.includes('MIME type') ||
      (message.includes('Cannot read properties of undefined') && (message.includes('default') || message.includes('call')));

    return { hasError: true, error, isStaleChunk };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    const message = error instanceof Error ? error.message : String(error);
    const isStaleChunk =
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Importing a module script failed') ||
      message.includes('Expected a JavaScript-or-Wasm module script') ||
      message.includes('Unexpected token <') ||
      message.includes('MIME type') ||
      (message.includes('Cannot read properties of undefined') && (message.includes('default') || message.includes('call')));

    if (isStaleChunk && typeof window !== 'undefined') {
      const marker = 'intouch-eb-stale-reload';
      const lastReload = sessionStorage.getItem(marker);
      const now = Date.now();
      if (!lastReload || now - Number(lastReload) > 10000) {
        sessionStorage.setItem(marker, String(now));
        console.warn('[InTouch] Stale build chunk caught by ErrorBoundary. Auto-reloading latest version...');
        window.location.reload();
      }
    }
  }

  private handleRetry = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    } else {
      this.setState({ hasError: false, error: undefined, isStaleChunk: false });
    }
  };

  private handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { isStaleChunk, error } = this.state;

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
          <div className="max-w-md w-full mx-auto text-center p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700">
            <div className="w-16 h-16 bg-blue-50 dark:bg-blue-950/50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl">
              {isStaleChunk ? '✨' : '🛡️'}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              {isStaleChunk ? 'InTouch Updated' : 'Something went wrong'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm leading-relaxed">
              {isStaleChunk
                ? 'A new build or update of InTouch is available. Reload the page to load the latest high-speed assets.'
                : 'An unexpected application error occurred. You can reload the page or return to your feed.'}
            </p>

            <div className="space-y-2">
              <button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg shadow-blue-500/25 transition-all cursor-pointer"
              >
                {isStaleChunk ? 'Update & Reload App' : 'Reload Page'}
              </button>

              {!isStaleChunk && (
                <button
                  onClick={this.handleGoHome}
                  className="w-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-medium py-2.5 px-6 rounded-xl transition-all cursor-pointer text-sm"
                >
                  Return to Home Feed
                </button>
              )}
            </div>

            {error && import.meta.env.DEV && (
              <details className="mt-4 text-left text-xs text-red-500 bg-red-50 dark:bg-red-950/30 p-3 rounded-lg border border-red-200 dark:border-red-900 overflow-x-auto">
                <summary className="cursor-pointer font-semibold mb-1">Developer Error Details</summary>
                <pre className="whitespace-pre-wrap">{error.stack || error.message}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}